/**
 * Mutually exclusive "Show" presets for the manifests list. Each preset maps to
 * omnisearch terms the M0/M1 backend understands. Counts for these presets are
 * loaded via `processedManifestShowFilterCounts` against the page scope
 * (tenant + year) only.
 */
export const SHOW_FILTER_IDS = [
  "allManifests",
  "overdueManifests",
  "awaitingEManifest",
  "awaitingEOverdueManifest",
  "unresolvedDiscrepancies",
  "pendingDiscrepancies",
  "transporterContacted",
  "exceptionReportFiled",
  "unlinkedManifests",
  "needsDocumentReview",
] as const;

export type ShowFilterId = (typeof SHOW_FILTER_IDS)[number];

/**
 * Omnisearch keys owned by Show presets — stripped before applying a new one.
 * `unresolvedDiscrepancyCount`/`pendingDiscrepancyCount` stay listed even
 * though no current preset emits them, so a bookmarked URL carrying the old
 * preset terms still gets stripped when the user picks a different preset.
 *
 * Every key here is preset vocabulary and nothing else. A key a grid column
 * also filters on must NOT be listed: stripping by key would then delete a
 * filter the user set from the column, which they never asked to clear.
 * `sourceDocumentState` is the one such key — see
 * {@link SHOW_FILTER_SHARED_COLUMN_KEYS}.
 */
export const SHOW_FILTER_OMNISEARCH_KEYS = [
  "correctionBucket",
  "isEManifest",
  "isUniformHazardousManifest",
  "unresolvedDiscrepancyCount",
  "pendingDiscrepancyCount",
  "showDiscrepancyStatus",
  "transporterContacted",
  "exceptionReportFiled",
  "hasFacility",
] as const;

/**
 * Preset vocabulary that a grid column also filters on, so the same token can
 * arrive from either control and the two are indistinguishable once written.
 *
 * `sourceDocumentState` backs both the "Needs document review" preset and the
 * Document Review column. These keys are cleared by matching a preset's exact
 * terms rather than by key, so picking a preset replaces a conflicting preset
 * value while leaving an unrelated column value (`:failed`, `:processing`)
 * alone. An identical token is a genuine tie, resolved toward the preset.
 */
export const SHOW_FILTER_SHARED_COLUMN_KEYS = ["sourceDocumentState"] as const;

export interface ShowFilterDefinition {
  id: ShowFilterId;
  label: string;
  /** One-line explanation shown as a hover tooltip on the dropdown item. */
  description: string;
  terms: readonly string[];
  /**
   * Terms an earlier version of this preset emitted. Still recognized by
   * {@link detectActiveShowFilter} so a bookmarked or saved URL keeps naming
   * the preset it was saved as. Never emitted — applying the preset writes
   * {@link ShowFilterDefinition.terms}.
   *
   * Without this the dropdown reads "All manifests" over a grid the old term
   * is still filtering (the handlers stay registered), and the reader cannot
   * correct it from the dropdown: MUI's Autocomplete returns early when the
   * chosen option already equals the current value, so picking "All manifests"
   * fires no change.
   */
  legacyTerms?: readonly string[];
}

export const SHOW_FILTERS: Record<ShowFilterId, ShowFilterDefinition> = {
  allManifests: {
    id: "allManifests",
    label: "All manifests",
    description: "Every manifest for the selected year — no status filter applied.",
    terms: [],
  },
  // "Corrections past due", not "Overdue corrections": the menu also carries
  // "Overdue e-Manifest" (a different, 45/60-day clock) two items below, and
  // the bare word was being read as the same deadline.
  overdueManifests: {
    id: "overdueManifests",
    label: "Corrections past due",
    description:
      "Manifests with an EPA correction request still unanswered more than 30 days after the request.",
    terms: ["correctionBucket:OVERDUE"],
  },
  awaitingEManifest: {
    id: "awaitingEManifest",
    label: "Awaiting e-Manifest",
    description: "Uniform-hazardous manifests with no e-Manifest record on file yet.",
    terms: ["isUniformHazardousManifest:true", "isEManifest:false"],
  },
  awaitingEOverdueManifest: {
    id: "awaitingEOverdueManifest",
    label: "Overdue e-Manifest",
    description:
      "Uniform-hazardous manifests still missing an e-Manifest past the 45/60-day deadline.",
    terms: ["isEManifest:overdue"],
  },
  unresolvedDiscrepancies: {
    id: "unresolvedDiscrepancies",
    label: "Manifests with discrepancies pending review",
    description:
      "Manifests with at least one discrepancy between sources that Encamp has not reviewed yet.",
    terms: ["showDiscrepancyStatus:UNRESOLVED"],
    legacyTerms: ["unresolvedDiscrepancyCount:>0"],
  },
  pendingDiscrepancies: {
    id: "pendingDiscrepancies",
    label: "Manifests with a fix pending",
    description:
      "Manifests with a discrepancy whose fix is underway — a compliance action (TSDF contacted, exception report) is in flight.",
    terms: ["showDiscrepancyStatus:PENDING"],
    legacyTerms: ["pendingDiscrepancyCount:>0"],
  },
  transporterContacted: {
    id: "transporterContacted",
    label: "TSDF contacted",
    description:
      "Manifests where the transporter/TSDF has been contacted about a missing e-Manifest.",
    terms: ["transporterContacted:true"],
  },
  exceptionReportFiled: {
    id: "exceptionReportFiled",
    label: "Exception Report Filed",
    description: "Manifests with a filed exception report for an overdue e-Manifest.",
    terms: ["exceptionReportFiled:true"],
  },
  unlinkedManifests: {
    id: "unlinkedManifests",
    label: "No facility link",
    description:
      "Manifests saved without a linked facility — the EPA ID didn't match a facility in Encamp (bad/unknown ID, or the facility isn't set up yet).",
    terms: ["hasFacility:false"],
  },
  // `sourceDocumentState` is a manifest-only column, so on the merged Shipment
  // Documents list this preset narrows to manifests — which is right, a bill of
  // lading has no document review.
  needsDocumentReview: {
    id: "needsDocumentReview",
    label: "Needs document review",
    description:
      "Manifests whose uploaded document has been read but not yet confirmed — the extracted data is waiting on a human.",
    terms: ["sourceDocumentState:needsReview"],
  },
};

export const SHOW_FILTER_ORDER: readonly ShowFilterId[] = SHOW_FILTER_IDS;

/**
 * The presets to offer, given whether the tenant has EPA correction
 * discrepancies.
 *
 * `overdueManifests` selects on `correctionBucket`, the EPA 30-day correction
 * clock. Every surface that renders a correction is gated on
 * `waste-epa-correction-discrepancies` — the manifest tile
 * (`ManifestDetailSection`), the customer dialog (`CustomerDiscrepancyDialog`),
 * the staff dialogs, and the vendor dashboard
 * (`useVendorDashboardEnabled`) — but the column the preset reads is not. With
 * the flag off the preset still matched, listing manifests whose Discrepancies
 * column is blank and whose detail tile says there are none, so a reader who
 * followed it had nowhere to go. Drop the preset rather than leave that dead
 * end: a tenant without the feature has nothing to show behind it.
 */
export function visibleShowFilterIds(epaCorrectionsEnabled: boolean): readonly ShowFilterId[] {
  if (epaCorrectionsEnabled) return SHOW_FILTER_ORDER;
  return SHOW_FILTER_ORDER.filter((id) => id !== "overdueManifests");
}

/** Presets checked first win; more specific filters must precede broader ones. */
const SHOW_FILTER_DETECTION_ORDER: readonly ShowFilterId[] = [
  "overdueManifests",
  "awaitingEOverdueManifest",
  "awaitingEManifest",
  "unresolvedDiscrepancies",
  "pendingDiscrepancies",
  "transporterContacted",
  "exceptionReportFiled",
  "needsDocumentReview",
  "unlinkedManifests",
  "allManifests",
];

function tokenize(search: string): string[] {
  return search.split(/\s+/).filter((token) => token.length > 0);
}

function tokenKey(token: string): string | null {
  const colonIdx = token.indexOf(":");
  if (colonIdx <= 0) return null;
  return token.substring(0, colonIdx);
}

/** Which Show preset matches the active omnisearch string, if any. */
export function detectActiveShowFilter(search: string): ShowFilterId {
  const tokens = tokenize(search);

  for (const id of SHOW_FILTER_DETECTION_ORDER) {
    const preset = SHOW_FILTERS[id];
    if (preset.terms.length === 0) {
      continue;
    }
    if (preset.terms.every((term) => tokens.includes(term))) {
      return id;
    }
  }

  // Second pass so a current term always wins over an older one carried in the
  // same URL. A saved link from before a preset changed its terms still names
  // the preset it was saved as, rather than reading "All manifests" over a grid
  // the old term is still filtering.
  for (const id of SHOW_FILTER_DETECTION_ORDER) {
    const legacyTerms = SHOW_FILTERS[id].legacyTerms ?? [];
    if (legacyTerms.length > 0 && legacyTerms.every((term) => tokens.includes(term))) {
      return id;
    }
  }

  const hasShowKey = tokens.some((token) => {
    const key = tokenKey(token);
    return (
      key != null &&
      SHOW_FILTER_OMNISEARCH_KEYS.includes(key as (typeof SHOW_FILTER_OMNISEARCH_KEYS)[number])
    );
  });
  return hasShowKey ? "allManifests" : "allManifests";
}

/**
 * Swap a preset's {@link ShowFilterDefinition.legacyTerms} for its current
 * terms, leaving everything else in `search` alone. Returns `search` unchanged
 * when no legacy term is present, so a caller can compare by identity.
 *
 * Detecting a legacy term is not enough on its own. The omnisearch handler for
 * the old term is still registered, so the grid stays filtered by it — the
 * count column, which totals every discrepancy kind including the staff-only
 * ones this preset now excludes. Relabeling alone would leave the dropdown
 * naming the corrected preset, and showing its corrected count, over rows the
 * old term selected; and because the dropdown then reads as already active,
 * choosing it fires no change (MUI's Autocomplete returns early when the
 * chosen option equals the current value), so the URL could never migrate
 * itself. Rewriting the token is what actually moves the grid onto the new
 * filter.
 *
 * Only the exact retired token migrates. A hand-written
 * `unresolvedDiscrepancyCount:>2` is a numeric filter in its own right and is
 * left as typed.
 */
export function migrateLegacyShowFilterTerms(search: string): string {
  const tokens = tokenize(search);

  for (const id of SHOW_FILTER_DETECTION_ORDER) {
    const legacyTerms = SHOW_FILTERS[id].legacyTerms ?? [];
    if (legacyTerms.length === 0) continue;
    if (!legacyTerms.every((term) => tokens.includes(term))) continue;

    const kept = tokens.filter((token) => !legacyTerms.includes(token));
    const added = SHOW_FILTERS[id].terms.filter((term) => !kept.includes(term));
    return kept.concat(added).join(" ");
  }

  return search;
}

/**
 * Replace Show-preset tokens in `search` with those from `filterId`.
 *
 * Preset-only keys go by key. Keys a column shares
 * ({@link SHOW_FILTER_SHARED_COLUMN_KEYS}) go by the outgoing preset's exact
 * terms, so a column filter the user set survives a preset change instead of
 * disappearing with it.
 */
export function applyShowFilterToSearch(search: string, filterId: ShowFilterId): string {
  const showKeySet = new Set<string>(SHOW_FILTER_OMNISEARCH_KEYS);
  const outgoing = detectActiveShowFilter(search);
  const outgoingTerms = new Set<string>([
    ...SHOW_FILTERS[outgoing].terms,
    ...(SHOW_FILTERS[outgoing].legacyTerms ?? []),
  ]);
  // The incoming preset's own keys clear whatever held them, column filter or
  // not: one key carrying two values contradicts itself on the server.
  const incomingKeys = new Set(
    SHOW_FILTERS[filterId].terms
      .map((term) => tokenKey(term))
      .filter((key): key is string => key != null)
  );
  const kept = tokenize(search).filter((token) => {
    if (outgoingTerms.has(token)) return false;
    const key = tokenKey(token);
    if (key == null) return true;
    return !showKeySet.has(key) && !incomingKeys.has(key);
  });
  const nextTerms = SHOW_FILTERS[filterId].terms;
  const merged = kept.concat(nextTerms.filter((term) => !kept.includes(term)));
  return merged.join(" ");
}

export function formatShowFilterLabel(label: string, count: number | undefined): string {
  if (count == null) return label;
  return `${label} (${count.toLocaleString()})`;
}
